import { API } from "../api";

// Privacy-first, self-owned analytics. Anonymous: a random id in localStorage
// (no PII, no account) + a per-tab session id. Fire-and-forget; never blocks UI.

const ANON_KEY = "fw_anon_id";
const INTERNAL_KEY = "fw_internal";

function uid() {
  return (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
}

// cookie helpers — a long-lived backup so a localStorage wipe doesn't re-count
// the same browser as a brand-new visitor.
function setCookie(name, value, days) {
  try {
    const exp = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${exp};path=/;SameSite=Lax`;
  } catch { /* ignore */ }
}
function getCookie(name) {
  try {
    return document.cookie.split("; ").reduce((acc, c) => {
      const [k, v] = c.split("=");
      return k === name ? decodeURIComponent(v) : acc;
    }, null);
  } catch { return null; }
}

export function getAnonId() {
  let id = localStorage.getItem(ANON_KEY) || getCookie(ANON_KEY);
  if (!id) id = uid();
  // re-persist in BOTH stores so whichever survives can restore the other
  try { localStorage.setItem(ANON_KEY, id); } catch { /* ignore */ }
  setCookie(ANON_KEY, id, 730);
  return id;
}

// Allow a device to opt out of analytics entirely (used to exclude the owner's
// own devices — e.g. the one used to view the admin dashboard).
export function setDeviceExcluded(excluded) {
  try {
    if (excluded) localStorage.setItem(INTERNAL_KEY, "1");
    else localStorage.removeItem(INTERNAL_KEY);
  } catch { /* ignore */ }
}
export function isDeviceExcluded() {
  try { return localStorage.getItem(INTERNAL_KEY) === "1"; } catch { return false; }
}

function getSessionId() {
  let id = sessionStorage.getItem("fw_session_id");
  if (!id) {
    id = uid();
    sessionStorage.setItem("fw_session_id", id);
  }
  return id;
}

// Native app (Capacitor iOS/Android) runs on a localhost origin but is a REAL
// user — detect it so it isn't dropped as dev traffic and is labelled correctly.
function isNativeApp() {
  try { return window.Capacitor?.isNativePlatform?.() === true; } catch { return false; }
}

// Don't record internal traffic: preview/localhost environments, automated
// browsers, or any device the owner has explicitly excluded.
function isInternal() {
  try {
    if (navigator.webdriver) return true;
    if (isDeviceExcluded()) return true;
    if (isNativeApp()) return false; // native app runs on localhost but is a real user
    const h = window.location.hostname || "";
    return (
      h.includes("preview.emergentagent.com") ||
      h === "localhost" ||
      h === "127.0.0.1" ||
      h.endsWith(".local")
    );
  } catch {
    return false;
  }
}

// Coarse, anonymous platform bucket — NO raw user-agent, device model, or IP.
// Just a category so the owner can see app vs web adoption.
export function platformBucket() {
  try {
    const ua = navigator.userAgent || "";
    const installed = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && "ontouchend" in document);
    if (isAndroid) return installed ? "Android app" : "Android browser";
    if (isIOS) return installed ? "iPhone app" : "iPhone browser";
    return installed ? "Installed app" : "Desktop web";
  } catch {
    return "Unknown";
  }
}

// Coarse, anonymous acquisition source — where a visit came from. Derived from
// the UTM source (ad campaigns) or the referring site, mapped to a friendly
// bucket. Captured once per session (the ENTRY source), never overwritten by
// later in-app navigation. No URLs or query strings stored — just the bucket.
const SOURCE_KEY = "fw_source";

function hostToSource(host) {
  const h = host.toLowerCase();
  if (h.includes("google")) return "Google";
  if (h.includes("facebook") || h === "fb.com" || h.includes("fb.me")) return "Facebook";
  if (h.includes("instagram")) return "Instagram";
  if (h.includes("bing")) return "Bing";
  if (h.includes("duckduckgo")) return "DuckDuckGo";
  if (h.includes("yahoo")) return "Yahoo";
  if (h === "t.co" || h.includes("twitter") || h === "x.com") return "Twitter / X";
  if (h.includes("youtube")) return "YouTube";
  if (h.includes("tiktok")) return "TikTok";
  if (h.includes("reddit")) return "Reddit";
  if (h.includes("pinterest")) return "Pinterest";
  if (h.includes("linkedin")) return "LinkedIn";
  return `Other: ${h.replace(/^www\./, "")}`;
}

function normalizeUtm(s) {
  const v = (s || "").toLowerCase();
  if (v.includes("google") || v === "adwords" || v === "gads") return "Google";
  if (v.includes("facebook") || v === "fb" || v === "meta" || v === "ig" || v === "instagram") {
    return v.includes("insta") || v === "ig" ? "Instagram" : "Facebook";
  }
  if (v.includes("bing")) return "Bing";
  if (v.includes("tiktok")) return "TikTok";
  if (v.includes("twitter") || v === "x") return "Twitter / X";
  if (v.includes("youtube")) return "YouTube";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function getSource() {
  try {
    const cached = sessionStorage.getItem(SOURCE_KEY);
    if (cached) return cached;
    let source;
    const params = new URLSearchParams(window.location.search);
    const utm = params.get("utm_source");
    if (utm) {
      source = normalizeUtm(utm);
    } else {
      const ref = document.referrer || "";
      if (!ref) {
        source = "Direct";
      } else {
        const host = new URL(ref).hostname || "";
        // referral from our own domain = internal navigation, treat as Direct entry
        source = host.includes(window.location.hostname) ? "Direct" : hostToSource(host);
      }
    }
    sessionStorage.setItem(SOURCE_KEY, source);
    return source;
  } catch {
    return "Direct";
  }
}

export function track(event, props = {}, path) {
  if (isInternal()) return;
  try {
    const body = JSON.stringify({
      anon_id: getAnonId(),
      session_id: getSessionId(),
      event,
      path: path || window.location?.pathname || "",
      platform: platformBucket(),
      props: { src: getSource(), ...props },
    });
    fetch(`${API}/analytics/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* analytics must never break the app */
  }
}

export function trackPageView(path) {
  track("page_view", {}, path);
}
